import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { getPayments } from "src/api/payments";
import { useAuth } from "src/providers/AuthProvider";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import CustomModal from "src/components/CustomModal";
import PaymentDetailModal from "src/components/modals/PaymentDetailModal";
import { DataTable, PageHead, SearchField, StatusBadge, type Column, type RowsPerPageOption } from "src/components/common";
import { getCustomizationValue, renderCurrency } from "src/lib/utils";
import type { PaymentRow } from "src/types/invoice";

const formatDate = (value?: string) => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "MMM dd, yyyy");
};

const Payments = () => {
	const { user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();

	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";
	const invoiceLabel = getCustomizationValue(user, "invoice_label", true) || "Invoice";

	const search = searchParams.get("search") ?? undefined;
	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);

	const { data, isLoading } = useQuery({
		queryKey: ["payments", page, limit, search],
		queryFn: () => getPayments({ page, limit: limit === "All" ? 9999 : limit, search }),
	});

	const list = data?.data;
	const payments = list?.data ?? [];
	const total = list?.total ?? payments.length;

	const columns: Column<PaymentRow>[] = [
		{
			key: "customer",
			header: `${customerLabel} Name`,
			primary: true,
			cell: (row) => [row.first_name, row.last_name].filter(Boolean).join(" ") || "—",
		},
		{ key: "invoice", header: invoiceLabel, cell: (row) => row.invoice_number ?? "—" },
		{ key: "method", header: "Payment Method", cell: (row) => <span className="tw-capitalize">{row.payment_method ?? "—"}</span> },
		{
			key: "amount",
			header: "Amount Paid",
			cell: (row) => <span className="tw-font-semibold">{renderCurrency(row.amount ?? 0)}</span>,
		},
		{ key: "date", header: "Payment Date", cell: (row) => formatDate(row.created_at) },
		{
			key: "status",
			header: "Status",
			cell: (row) => <StatusBadge status={row.status === "success" ? "paid" : "pending"} label={row.status ?? "—"} />,
		},
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[90px] tw-text-right",
			// Payments made outside an invoice have nothing to show, so they get no View action.
			cell: (row) =>
				row.invoice_uuid ? (
					<CustomModal
						title="Payment Details"
						size="normal"
						trigger={
							<button
								type="button"
								className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
							>
								View
							</button>
						}
					>
						<PaymentDetailModal paymentId={row.invoice_uuid} />
					</CustomModal>
				) : null,
		},
	];

	return (
		<div className="tw-space-y-1">
			<PageHead
				title="Payments"
				subtitle={isLoading ? "Loading..." : `${total} ${total === 1 ? "payment" : "payments"} recorded`}
			/>

			<div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3 tw-py-4">
				<SearchField placeholder="Search payments..." />
			</div>

			<DataTable
				data={payments}
				columns={columns}
				rowKey={(row, index) => row.uuid ?? index}
				loading={isLoading}
				totalRows={total}
				page={page}
				onPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("page", String(next));
					setSearchParams(params);
				}}
				rowsPerPage={limit}
				onRowsPerPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("limit", String(next));
					params.set("page", "1");
					setSearchParams(params);
				}}
				emptyTitle={search ? `No payments match "${search}"` : "No payments yet"}
				emptyDescription={
					search ? "Try a different search term." : "Confirmed payments against your invoices will appear here."
				}
			/>
		</div>
	);
};

export default withPermissionWrapper(Payments, "view_payment");
